// plan-modusign-style-acceptance §Phase1 — Template management API client.

import type {
  CreateSignatureTemplateDto,
  SignatureDocumentType,
  SignatureField,
  SignerRole,
  SignatureTemplateDetailDto,
  SignatureTemplateSummaryDto,
  UpdateSignatureTemplateResponseDto,
  LayoutObject,
  TemplateLayout,
} from '@bhops/shared';
import { apiClient } from './client';

export interface ListTemplatesQuery {
  documentType?: SignatureDocumentType;
  includeArchived?: boolean;
}

/** 개선계획 §5-2 — 중복 구버전 일괄 보관 드라이런/실행 리포트. */
export interface VersionCleanupItem {
  lineageId: string;
  name: string;
  documentType: SignatureDocumentType;
  keepId: string;
  keepVersion: number;
  archiveIds: string[];
  archiveVersions: number[];
}

export interface VersionCleanupReport {
  lineageCount: number;
  rowCount: number;
  items: VersionCleanupItem[];
}

export const signatureTemplatesApi = {
  async list(query: ListTemplatesQuery = {}): Promise<SignatureTemplateSummaryDto[]> {
    const { data } = await apiClient.get<SignatureTemplateSummaryDto[]>('/signature-templates', {
      params: query,
    });
    return data;
  },

  async get(id: string): Promise<SignatureTemplateDetailDto> {
    const { data } = await apiClient.get<SignatureTemplateDetailDto>(`/signature-templates/${id}`);
    return data;
  },

  async create(dto: CreateSignatureTemplateDto): Promise<{ id: string }> {
    const { data } = await apiClient.post<{ id: string }>('/signature-templates', dto);
    return data;
  },

  async requestUploadUrl(
    id: string,
    filename: string,
    byteSize: number,
  ): Promise<{ uploadUrl: string; s3Key: string; headers: Record<string, string> }> {
    const { data } = await apiClient.post<{
      uploadUrl: string;
      s3Key: string;
      headers: Record<string, string>;
    }>(`/signature-templates/${id}/upload-url`, {
      filename,
      contentType: 'application/pdf',
      byteSize,
    });
    return data;
  },

  /**
   * 개선계획 §5-3 — PDF 교체는 진행 중 문서가 있으면 새 버전으로 갈라진다.
   * newVersionCreated 면 호출부는 반환된 새 id 로 이동해야 한다.
   */
  async confirmUpload(
    id: string,
    s3Key: string,
    pageCount: number,
  ): Promise<UpdateSignatureTemplateResponseDto> {
    const { data } = await apiClient.post<UpdateSignatureTemplateResponseDto>(
      `/signature-templates/${id}/upload-complete`,
      { s3Key, pageCount },
    );
    return data;
  },

  /**
   * 엑셀(.xlsx) → 캔버스 객체(표). 저장은 하지 않는다 — 돌려받은 객체를 캔버스에
   * 놓고, 사람이 손본 뒤 `buildLayout` 으로 저장한다.
   *
   * 파일은 PDF 처럼 S3 로 올린 뒤 서버가 읽는다 — 사진이 든 엑셀은 1~2MB 라 본문에
   * 실으면 한도(2MB)를 넘는다. 엑셀에 박힌 사진은 서버가 올려 두고 `assetUrls` 로
   * 보여 줄 주소를 준다.
   */
  async xlsxToLayout(
    file: File,
    defaultSlot: string,
  ): Promise<{ objects: LayoutObject[]; notes: string[]; assetUrls: Record<string, string> }> {
    if (file.size > 15 * 1024 * 1024) {
      throw new Error('엑셀 파일이 너무 큽니다. 15MB까지 올릴 수 있습니다.');
    }
    const { data: up } = await apiClient.post<{
      uploadUrl: string;
      s3Key: string;
      headers: Record<string, string>;
    }>('/signature-templates/xlsx-upload-url', { filename: file.name, byteSize: file.size });
    await signatureTemplatesApi.uploadPdf(up.uploadUrl, file, up.headers);
    const { data } = await apiClient.post<{
      objects: LayoutObject[];
      notes: string[];
      assetUrls: Record<string, string>;
    }>('/signature-templates/xlsx-to-layout', {
      s3Key: up.s3Key,
      fileName: file.name,
      defaultSlot,
    });
    return data;
  },

  /** 캔버스 객체로 베이스 PDF 를 만든다. `newVersionCreated` 면 새 버전 id 로 옮겨 가야 한다. */
  async buildLayout(
    id: string,
    layout: TemplateLayout,
  ): Promise<UpdateSignatureTemplateResponseDto> {
    const { data } = await apiClient.post<UpdateSignatureTemplateResponseDto>(
      `/signature-templates/${id}/build-layout`,
      { layout },
    );
    return data;
  },

  /** 사진 객체용 이미지 presigned PUT URL. */
  async requestAssetUploadUrl(
    id: string,
    file: File,
  ): Promise<{ uploadUrl: string; s3Key: string; headers: Record<string, string> }> {
    const { data } = await apiClient.post<{
      uploadUrl: string;
      s3Key: string;
      headers: Record<string, string>;
    }>(`/signature-templates/${id}/asset-upload-url`, {
      filename: file.name,
      byteSize: file.size,
      contentType: file.type,
    });
    return data;
  },

  /** 개선계획 §5-2 — 이 양식이 속한 계보의 전 버전 이력(최신 포함). */
  async versions(id: string): Promise<SignatureTemplateSummaryDto[]> {
    const { data } = await apiClient.get<SignatureTemplateSummaryDto[]>(
      `/signature-templates/${id}/versions`,
    );
    return data;
  },

  /** 개선계획 §5-1 — 양식 복사(S3 PDF 까지 복사, 새 계보 v1). */
  async duplicate(id: string): Promise<{ id: string }> {
    const { data } = await apiClient.post<{ id: string }>(`/signature-templates/${id}/duplicate`);
    return data;
  },

  /** 개선계획 §5-2 — 과거 버전 내용으로 **새 버전**을 만든다(과거 행은 그대로). */
  async restoreVersion(id: string): Promise<UpdateSignatureTemplateResponseDto> {
    const { data } = await apiClient.post<UpdateSignatureTemplateResponseDto>(
      `/signature-templates/${id}/restore-version`,
    );
    return data;
  },

  /** 개선계획 §5-2 — 중복 구버전 일괄 보관: 드라이런(아무것도 바꾸지 않음). */
  async previewVersionCleanup(): Promise<VersionCleanupReport> {
    const { data } = await apiClient.post<VersionCleanupReport>(
      '/signature-templates/version-cleanup/preview',
    );
    return data;
  },

  /** 개선계획 §5-2 — 중복 구버전 일괄 보관: 실제 실행. */
  async applyVersionCleanup(): Promise<VersionCleanupReport> {
    const { data } = await apiClient.post<VersionCleanupReport>(
      '/signature-templates/version-cleanup/apply',
    );
    return data;
  },

  async update(
    id: string,
    dto: {
      name?: string;
      fieldsJson?: SignatureField[];
      signerRolesJson?: SignerRole[];
      /** 개선계획 §5-4 (결정 ⑥-1A) — '첨부 자리' 사용 여부. */
      allowAttachment?: boolean;
    },
  ): Promise<UpdateSignatureTemplateResponseDto> {
    const { data } = await apiClient.patch<UpdateSignatureTemplateResponseDto>(
      `/signature-templates/${id}`,
      dto,
    );
    return data;
  },

  async archive(id: string): Promise<void> {
    await apiClient.delete(`/signature-templates/${id}`);
  },

  /** Round 2 Phase G (#5, D-6) — 보관된 양식 복원(archivedAt 해제). */
  async restore(id: string): Promise<void> {
    await apiClient.post(`/signature-templates/${id}/restore`);
  },

  /** Round 2 Phase G (#5, D-6) — FK 가드된 완전 삭제(참조 문서 없을 때만). */
  async hardDelete(id: string): Promise<void> {
    await apiClient.delete(`/signature-templates/${id}/hard`);
  },

  /**
   * Upload PDF bytes directly to S3 using the presigned PUT URL.
   *
   * §F1 hotfix — previously used `fetch` without checking `res.ok`,
   * so 4xx/5xx from S3 were silently ignored (then confirmUpload
   * saved an s3Key pointing to nothing). Now forwards the signed
   * headers (content-type + content-length must match what the
   * server signed) and throws with the S3 response body on failure.
   */
  async uploadPdf(
    uploadUrl: string,
    file: File,
    signedHeaders: Record<string, string>,
  ): Promise<void> {
    const headers = new Headers();
    for (const [k, v] of Object.entries(signedHeaders)) {
      headers.set(k, v);
    }
    let res: Response;
    try {
      res = await fetch(uploadUrl, { method: 'PUT', body: file, headers });
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'network error';
      throw new Error(`파일을 올리지 못했습니다. 인터넷 연결을 확인해 주세요. (${reason})`);
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const snippet = text.slice(0, 240).replace(/\s+/g, ' ').trim();
      throw new Error(
        `파일을 올리지 못했습니다. 잠시 후 다시 시도해 주세요. (${res.status} ${res.statusText} ${snippet})`,
      );
    }
  },
};
